import Button from '@orn/shared-web/components/Button/Button';
import { HookForm, FormField } from '@orn/shared-web/components/Form';
import { OTPInput, PasswordChecklist, PasswordInput } from '@orn/shared-web/components/Input';
import validatePassword from '@orn/shared-web/lib/validation/passwordValidation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import useAuthApi from '../../../hooks/useAuthApi';
import useCountdown from '../../../hooks/useCountdown';
import LoginHeader from '../LoginHeader';
import LoginTitle from '../LoginTitle';
import { OTP_CODE_LENGTH, RESEND_COUNTDOWN_SECONDS } from '../types';

interface INewPasswordStepProps {
  email: string;
  requiresSmsOtp?: boolean;
  onSuccess: () => void;
  onSmsOtpRequired: (data: { emailCode: string; newPassword: string }) => void;
  onBack: () => void;
}

interface INewPasswordFormValues {
  password: string;
  confirmPassword: string;
}

const NewPasswordStep = ({ email, requiresSmsOtp, onSuccess, onSmsOtpRequired, onBack }: INewPasswordStepProps) => {
  const authApi = useAuthApi();
  const countdown = useCountdown();
  const [otpValue, setOtpValue] = React.useState('');
  const [otpError, setOtpError] = React.useState<string | undefined>();
  const [serverPasswordError, setServerPasswordError] = React.useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<INewPasswordFormValues>({
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const passwordValue = form.watch('password');

  // Start countdown on mount (code was just sent from ForgotPasswordStep)
  React.useEffect(() => {
    countdown.start(RESEND_COUNTDOWN_SECONDS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = React.useCallback(async () => {
    setOtpError(undefined);
    const result = await authApi.forgotPassword({ email });
    if (result.ok) {
      countdown.start(RESEND_COUNTDOWN_SECONDS);
    } else {
      setOtpError(result.error.error);
    }
  }, [authApi, email, countdown]);

  const handleSubmit = React.useCallback(
    async (values: INewPasswordFormValues) => {
      setOtpError(undefined);
      setServerPasswordError(undefined);

      if (otpValue.length !== OTP_CODE_LENGTH) {
        setOtpError('Please enter the 6-character code.');
        return;
      }

      const { rules, maxLengthError } = validatePassword(values.password);
      if (!rules.every((r) => r.met) || maxLengthError) {
        return;
      }

      if (values.password !== values.confirmPassword) {
        form.setError('confirmPassword', { message: 'Passwords do not match.' });
        return;
      }

      setIsSubmitting(true);

      if (requiresSmsOtp) {
        // Two-step flow: validate email OTP and trigger SMS before proceeding
        const smsResult = await authApi.sendPasswordResetSms({ email, code: otpValue });
        setIsSubmitting(false);

        if (!smsResult.ok) {
          if (smsResult.error.code === 'AUTH_INVALID_OTP') {
            setOtpError('Invalid or expired code. Please try again.');
            setOtpValue('');
          } else {
            setOtpError(smsResult.error.error);
          }
          return;
        }

        // Transition to SMS OTP step, carrying email code + new password
        onSmsOtpRequired({ emailCode: otpValue, newPassword: values.password });
        return;
      }

      // Single-step flow (no verified phone): reset immediately
      const result = await authApi.resetPassword({
        email,
        emailCode: otpValue,
        newPassword: values.password,
      });
      setIsSubmitting(false);

      if (!result.ok) {
        if (result.error.code === 'AUTH_INVALID_OTP') {
          setOtpError('Invalid or expired code. Please try again.');
          setOtpValue('');
        } else if (result.error.code === 'AUTH_WEAK_PASSWORD') {
          setServerPasswordError(result.error.error);
        } else {
          setOtpError(result.error.error);
        }
        return;
      }

      onSuccess();
    },
    [authApi, email, form, onSuccess, onSmsOtpRequired, otpValue, requiresSmsOtp]
  );

  const passwordValidation = validatePassword(passwordValue);
  const isPasswordValid = passwordValidation.rules.every((r) => r.met) && !passwordValidation.maxLengthError;

  return (
    <>
      <LoginHeader onBack={onBack} />
      <LoginTitle title="Reset your password" subtitle={`Enter the code sent to ${email}`} />

      <HookForm form={form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex w-full flex-col gap-4">
          <div className="flex flex-col gap-2">
            <span className="text-primary text-sm font-medium">Verification code</span>
            <OTPInput
              value={otpValue}
              onChange={setOtpValue}
              isInvalid={Boolean(otpError)}
              errorMessage={otpError}
              autoFocus
            />
            <div className="flex items-center">
              {countdown.isActive ? (
                <span className="text-tertiary text-sm">Resend code in {countdown.secondsRemaining}s</span>
              ) : (
                <Button color="link-color" size="sm" onClick={handleResend} type="button">
                  Resend code
                </Button>
              )}
            </div>
          </div>

          <FormField control={form.control} name="password">
            {({ field }) => (
              <PasswordInput {...field} label="New password" size="md" isRequired autoComplete="new-password" />
            )}
          </FormField>

          <FormField control={form.control} name="confirmPassword">
            {({ field, fieldState }) => (
              <PasswordInput
                {...field}
                label="Confirm password"
                size="md"
                isRequired
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message}
                autoComplete="new-password"
              />
            )}
          </FormField>

          <PasswordChecklist
            password={passwordValue}
            confirmPassword={form.watch('confirmPassword')}
            serverError={serverPasswordError}
          />

          <Button
            type="submit"
            size="xl"
            color="primary"
            className="w-full"
            isDisabled={
              !isPasswordValid || otpValue.length !== OTP_CODE_LENGTH || passwordValue !== form.watch('confirmPassword')
            }
            isLoading={isSubmitting}
          >
            {requiresSmsOtp ? 'Continue' : 'Reset password'}
          </Button>
        </div>
      </HookForm>
    </>
  );
};

NewPasswordStep.displayName = 'NewPasswordStep';

export default NewPasswordStep;
